import {
  AccessTime as AccessTimeIcon,
  WifiOff as WifiOffIcon,
} from '@mui/icons-material';
import {
  Container,
  CssBaseline,
  Box,
  Avatar,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { authApi } from 'api';
import { ROUTE_PATH } from 'constants/routes';
import { SessionExpiredReason } from 'enums';
import { TranslationNamespace } from 'i18n';
import { AuthData, authUtils } from 'utils';

export const SessionExpiredPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'pages',
  });

  const location = useLocation();
  const navigate = useNavigate();

  const connectionLostData = useMemo(
    () => authUtils.getConnectionLostData(),
    [],
  );

  const emailFromState = useMemo(() => location.state?.email, [location]);
  const reasonFromState = useMemo(
    () => location.state?.reason as SessionExpiredReason | undefined,
    [location],
  );
  const returnUrl = useMemo(
    () => location.state?.returnUrl as string | undefined,
    [location],
  );

  const isConnectionLost = useMemo(
    () => !!connectionLostData,
    [connectionLostData],
  );
  const email = useMemo(
    () => connectionLostData?.email || emailFromState,
    [connectionLostData, emailFromState],
  );

  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState(false);

  const openLogin = useCallback(() => {
    authUtils.clearConnectionLostData();
    navigate(ROUTE_PATH.PUBLIC.LOGIN, { state: { email, returnUrl } });
  }, [email, returnUrl, navigate]);

  useEffect(() => {
    if (!email) {
      openLogin();
    }
  }, [email, openLogin]);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    setRetryError(false);
    try {
      const authData: AuthData = await authApi.refreshAccessToken(
        connectionLostData?.accessToken,
      );
      const previousPath = connectionLostData?.previousPath;
      authUtils.setAuthData(authData);
      authUtils.clearConnectionLostData();
      navigate(previousPath || ROUTE_PATH.PUBLIC.LOGIN, { replace: true });
    } catch {
      setRetryError(true);
    } finally {
      setIsRetrying(false);
    }
  }, [connectionLostData, navigate]);

  const handleLogout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Best effort to clear cookie
    } finally {
      authUtils.clearConnectionLostData();
      navigate(ROUTE_PATH.PUBLIC.LOGIN, { state: { email, returnUrl } });
    }
  }, [email, returnUrl, navigate]);

  if (isConnectionLost) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'warning.main' }}>
            <WifiOffIcon />
          </Avatar>
          <div className="tw-my-8 tw-text-center">
            <div className="tw-text-xl">{t('connection_lost.title')}</div>
            <div className="tw-mt-4">{t('connection_lost.subtitle')}</div>
          </div>
          {retryError && (
            <Alert severity="error" className="tw-mb-4 tw-w-full">
              {t('connection_lost.error')}
            </Alert>
          )}
          <div className="tw-flex tw-gap-4">
            <Button
              color="primary"
              variant="contained"
              onClick={handleRetry}
              disabled={isRetrying}
              startIcon={
                isRetrying ? <CircularProgress size={20} /> : undefined
              }
            >
              {t('connection_lost.buttons.refresh')}
            </Button>
            <Button
              color="error"
              variant="outlined"
              onClick={handleLogout}
              disabled={isRetrying}
            >
              {t('connection_lost.buttons.logout')}
            </Button>
          </div>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'error.main' }}>
          <AccessTimeIcon />
        </Avatar>
        <div className="tw-my-8 tw-text-center">
          <div className="tw-text-xl">
            {reasonFromState === SessionExpiredReason.IpChanged
              ? t('session_expired.title_ip_changed')
              : t('session_expired.title')}
          </div>
          <div className="tw-mt-4">
            {reasonFromState === SessionExpiredReason.IpChanged
              ? t('session_expired.subtitle_ip_changed')
              : t('session_expired.subtitle')}
          </div>
        </div>
        <div>
          <Button color="primary" variant="contained" onClick={openLogin}>
            {t('session_expired.buttons.open_login')}
          </Button>
        </div>
      </Box>
    </Container>
  );
};
